import { get, isNil } from "lodash-es";
import * as moment from "moment-timezone";
import * as Yup from "yup";
import IDashboardDataUIDTO from "@models/DTO/IDashboardDataUIDTO";
import IOnLoadDashboardDTO from "@models/DTO/IOnLoadDashboardDTO";
import IOnLoadUserDTO from "@models/DTO/IOnLoadUserDTO";
import IConnection_Token from "@models/IConnection_Token";
import IDashboard from "@models/IDashboard";
import IOrdersAtStep from "@models/IOrdersAtStep";
import ISupplierModel from "@models/ISupplierModel";
import IUserUI from "@models/IUserUI";
import IVendorModel from "@models/IVendorModel";
import systemConst from "@services/SystemConst";

import IAppConnectParams from "@models/IAppConnectParams";
import TokenTypesEnum from "@models/TokenTypesEnum";
import { Socket } from "socket.io-client";
import EndpointsEnum from "@models/EndpointsEnum";
import IUserLeaveAllRoomsDTO from "@models/DTO/IUserLeaveAllRoomsDTO";
import ICatalog_Item from "@models/ICatalog_Item";
import AdminService from "@services/AdminService";

function getCookie(name: any) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === " ") {
            c = c.substring(1, c.length);
        }
        if (c.indexOf(nameEQ) === 0) {
            return c.substring(nameEQ.length, c.length);
        }
    }
    return null;
}

function eraseCookie(name: any) {
    let domain = "localhost";

    if (window.location.hostname.indexOf("192.168") >= 0) {
        domain = window.location.hostname;
    } else if (window.location.hostname !== "localhost") {
        domain = ".orderwerks.com";
    }

    document.cookie = name + "=; Max-Age=-99999999; domain=" + domain;
}

const getLocalUserData = (): Promise<any> => {
    return new Promise((resolve, reject) => {
        try {
            const userCookie = getCookie("orderwerks-user");

            let user: any = localStorage.getItem("user");

            console.log("getLocalUserData userCookie: ", userCookie);

            if (!isNil(userCookie)) {
                user = userCookie;
                eraseCookie("orderwerks-user");
            }

            const parsedUser = JSON.parse(user);

            if (!isNil(userCookie)) {
                saveUserData(parsedUser);
            }

            resolve(parsedUser);
        } catch (err) {
            reject(err);
        }
    });
};

const getLocalAppConnectDataFn = (): IAppConnectParams | null => {
    const appConnectCookieStr = getCookie("orderwerks-app-connect");

    let appConnectCookie: IAppConnectParams | null = null;

    if (appConnectCookieStr) {
        appConnectCookie = JSON.parse(appConnectCookieStr) as IAppConnectParams;
        eraseCookie("orderwerks-app-connect");
    }

    return appConnectCookie;
};

const setupLocalStorageLogoutEvent = () => {
    localStorage.setItem("logout-event", "logout" + Math.random());
};

const getLocalUserDataFn = (): IUserUI => {
    const userCookie = getCookie("orderwerks-user");

    let user: any = localStorage.getItem("user");

    if (!isNil(userCookie)) {
        user = userCookie;
        eraseCookie("orderwerks-user");
    }

    // If not in localStorage, check the URL parameters (likely coming from login redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    const guid = urlParams.get("guid");

    if (!isNil(token) && !isNil(guid)) {
        // Found user data in URL parameters
        const parsedUser = { token, guid }; // Construct the user object
        saveUserData(parsedUser);
        return parsedUser as IUserUI;
    } else {
        const parsedUser = JSON.parse(user);

        if (!isNil(userCookie)) {
            saveUserData(parsedUser);
        }

        return parsedUser;
    }
};

const getDashboardData = (): IDashboardDataUIDTO => {
    const dashboardData = localStorage.getItem("dashboardData") as string | null;

    if (dashboardData) {
        const parsedDashboardData = JSON.parse(dashboardData);

        return parsedDashboardData;
    } else {
        return {} as IDashboardDataUIDTO;
    }
};

/**
 * Properly saves the users data that's returned from the savePreferences for a user
 * so we don't lose any of the supplementary details, and it updates the localStorage
 *
 * @param user
 */
const profileSaveUserData = (user: any) => {
    const storedUser: any = localStorage.getItem("user");
    const parsedStoredUser = JSON.parse(storedUser);

    user.isSupplierUser = parsedStoredUser.isSupplierUser;
    user.userType = parsedStoredUser.userType;
    user.supplierUserToken = parsedStoredUser.supplierUserToken;
    user.vendorUserToken = parsedStoredUser.vendorUserToken;
    user.userSwitchTokens = parsedStoredUser.userSwitchTokens || [];

    user.dataAge = Date.now();

    const userData = JSON.stringify(user);

    localStorage.setItem("user", userData);

    return user;
};

const saveUserData = (data: any) => {
    // return new Promise((resolve, reject) => {
    try {
        data.dataAge = Date.now();

        const userData = JSON.stringify(data);

        localStorage.setItem("user", userData);

        return true;
        // resolve(true);
    } catch (err) {
        // reject(err);
        return err;
    }
    // });
};

const saveForceOnline = (forceOnline: boolean) => {
    try {
        localStorage.setItem("forceOnline", forceOnline ? "true" : "false");

        return true;
    } catch (err) {
        return err;
    }
};

const getForceOnline = (): boolean => {
    const item = localStorage.getItem("forceOnline");

    return item === "true";
};

const saveDashboardData = async (data: IDashboardDataUIDTO) => {
    // return new Promise((resolve, reject) => {
    try {
        data.dataAge = Date.now();

        const dashboardData = JSON.stringify(data);

        localStorage.setItem("dashboardData", dashboardData);

        return true;
        // resolve(true);
    } catch (err) {
        // reject(err);
        return err;
    }
    // });
};

const clearUserData = () => {
    return new Promise((resolve, reject) => {
        try {
            localStorage.clear();

            resolve(true);
        } catch (err) {
            reject(err);
        }
    });
};

const parseUTCDate = (dateStr: any): string => {
    if (!dateStr) {
        return "";
    }

    return moment.utc(dateStr).format("MM/DD/YYYY").toString();
};

const parseDate = (dateStr: any): string => {
    if (!dateStr) {
        return "";
    }

    return parseLastUpdated(dateStr);
};

const parseUnixTimestamp = (dateStr: any, includeTime: boolean = false, twentyFourHour: boolean = true): string => {
    // TODO pull the users profile preference to parse in that format.
    const userModel = getLocalUserDataFn();
    const prefTimezone = get(userModel, "pref_timezone", null);
    const dateMomentObj = moment.unix(dateStr);
    let parsedMoment;

    let format = "MM/DD/YYYY";

    if (includeTime) {
        if (twentyFourHour) {
            format = "MM/DD/YYYY HH:mm:ss";
        } else {
            format = "MM/DD/YYYY hh:mm:ssa";
        }
    }

    if (prefTimezone !== "utc") {
        if (!isNil(prefTimezone) && prefTimezone.length) {
            parsedMoment = dateMomentObj.tz(prefTimezone).format(format).toString();
        } else {
            parsedMoment = dateMomentObj.local().format(format).toString();
        }
    } else {
        parsedMoment = dateMomentObj.format(format).toString();
    }

    return parsedMoment;
};

const parseLastUpdated = (
    dateStr: any,
    includeTime: boolean = false,
    twentyFourHour: boolean = true,
    includeTimeZoneAbbr: boolean = false,
    separators: string = "/"
): string => {
    // TODO pull the users profile preference to parse in that format.
    const userModel = getLocalUserDataFn();
    const prefTimezone = get(userModel, "pref_timezone", null);
    const dateMomentObj = moment.utc(dateStr, systemConst.defaultValue.dateFormat);
    let parsedMoment;

    let format = `MM${separators}DD${separators}YYYY`;

    if (includeTime) {
        if (twentyFourHour) {
            format = `MM${separators}DD${separators}YYYY HH:mm:ss`;
        } else {
            format = `MM${separators}DD${separators}YYYY hh:mm:ssa`;
        }
    }

    if (prefTimezone !== "utc") {
        if (!isNil(prefTimezone) && prefTimezone.length) {
            parsedMoment = dateMomentObj.tz(prefTimezone).format(format).toString();
            if (includeTimeZoneAbbr) {
                parsedMoment += " " + dateMomentObj.tz(prefTimezone).zoneAbbr();
            }
        } else {
            parsedMoment = dateMomentObj.local().format(format).toString();
            if (includeTimeZoneAbbr) {
                parsedMoment += " " + dateMomentObj.local().zoneAbbr();
            }
        }
    } else {
        parsedMoment = dateMomentObj.format(format).toString();
        if (includeTimeZoneAbbr) {
            parsedMoment += " " + dateMomentObj.zoneAbbr();
        }
    }

    return parsedMoment;
};

const performLogout = (options?: { withReason?: any; reason?: any; navTo?: any; socket?: Socket; userModel?: IUserUI }) => {
    // Clear admin session before clearing all localStorage
    AdminService.clearAdminLogin();

    localStorage.clear();
    eraseCookie("orderwerks-user");

    UserService.setupLocalStorageLogoutEvent();

    let url = "http://localhost:8080/login";

    if (window.location.hostname === "app.orderwerks.com") {
        url = "https://www.orderwerks.com/login";
    } else if (window.location.hostname.indexOf("test") !== -1) {
        url = `https://test.orderwerks.com/login`;
    } else if (window.location.hostname.indexOf("ngrok.io") !== -1) {
        url = `https://orderwerks-site.ngrok.io/login`;
    } else if (window.location.hostname !== "localhost") {
        url = `http://${window.location.hostname}:8080/login`;
    }

    if (!isNil(options?.withReason) && options?.withReason) {
        if (!isNil(options?.reason)) {
            url += `?reason=${options?.reason}`;
        } else {
            url += "?reason=inactive";
        }
    }

    if (!isNil(options?.navTo)) {
        if (!isNil(options?.withReason)) {
            url += `&navTo=${options?.navTo}`;
        } else {
            url += `?navTo=${options?.navTo}`;
        }
    }

    // close all open sockets and routes.
    if (options?.socket) {
        const payload: IUserLeaveAllRoomsDTO = {
            userGuid: options.userModel?.guid || "",
            userToken: options.userModel?.token || "",
        };

        options.socket.emit(EndpointsEnum.UserLeaveAllRooms, payload);
    }

    console.log("performLogout url: ", url);

    window.location.href = url;
};

const transformUserData = async (
    data: IOnLoadUserDTO
): Promise<{
    userModel: IUserUI;
    supplier: ISupplierModel;
    supplierModel: ISupplierModel;
    vendor: IVendorModel;
    vendorModel: IVendorModel;
    isSupplierUser: boolean;
    isVendorSourceUser: boolean;
    name: string;
    supplierUserToken: IConnection_Token;
    vendorUserToken: IConnection_Token;
    vendorSourceUserToken: IConnection_Token | undefined;
    supplierVendorToken: IConnection_Token | undefined;
}> => {
    //@ts-expect-error
    const user = data.user as IUserUI;
    const vendor = data.vendor || {};
    const supplier = data.supplier || {};
    const isSupplierUser = data.isSupplierUser;
    const userType = data.userType;
    const supplierUserToken = data.supplierUserToken;
    const vendorUserToken = data.vendorUserToken;
    const isVendorSourceUser = data.isVendorSourceUser;
    const vendorSourceUserToken = data.vendorSourceUserToken;
    const supplierVendorToken = data.vendor.Connection_Tokens?.find((token) => token.token_type_id === TokenTypesEnum.SUPPLIER_VENDOR);

    user.isSupplierUser = isSupplierUser;
    //@ts-expect-error
    user.userType = userType;
    user.supplierUserToken = data.supplierUserToken;
    user.vendorUserToken = data.vendorUserToken;
    user.isVendorSourceUser = isVendorSourceUser;

    user.userSwitchTokens = (data.userSwitchTokens || []).filter((token: IConnection_Token) => {
        // filter out any of the Supplier Owners Vendor Master Tokens (aka House Account)
        // as they won't ever need to view as the house account vendor.
        if (token.is_master && token.Vendor) {
            // do nothing?
        } else {
            return token;
        }
    });

    // //@ts-expect-error
    // if (Rollbar) {
    //     //@ts-expect-error
    //     Rollbar.configure({
    //         payload: {
    //             person: {
    //                 id: user.guid,
    //                 username: user.name,
    //                 email: user.email,
    //             },
    //         },
    //     });
    // }

    return {
        isSupplierUser,
        name: user.name,
        supplier,
        supplierModel: supplier,
        supplierUserToken,
        userModel: user,
        vendor,
        vendorModel: vendor,
        vendorUserToken,
        isVendorSourceUser,
        vendorSourceUserToken,
        supplierVendorToken,
    };
};

const transformDashboardData = async (
    data: IOnLoadDashboardDTO
): Promise<{
    orderVolume: any[];
    shipmentVolume: any[];
    recentOrders: any[];
    systemMessages: any[];
    alertOrders: any[];
    totalClients: number;
    totalOrders: number;
    totalProjects: number;
    totalVendors: number;
    totalMonthlyOrders: number;
    totalMonthlyShipments: number;
    lastFiveStock: Partial<ICatalog_Item>[];
    coopTotal: number;
    rebateTotal: number;
    dashboards: IDashboard[];
    dailySales: number;
    monthlySales: number;
    weeklySales: number;
    ordersAtStep: IOrdersAtStep;
}> => {
    const dashboards = data.dashboards;

    const {
        orderVolume = [],
        shipmentVolume = [],
        recentOrders = [],
        systemMessages = [],
        totalClients = 0,
        totalOrders = 0,
        totalProjects = 0,
        totalVendors = 0,
        totalMonthlyOrders,
        totalMonthlyShipments,
        lastFiveStock = [],
        coopTotal = 0,
        rebateTotal = 0,
        dailySales = 0,
        monthlySales = 0,
        weeklySales = 0,
        ordersAtStep = {},
        alertOrders = [],
    } = data.dashboard;

    saveDashboardData(data.dashboard as IDashboardDataUIDTO);

    return {
        orderVolume,
        recentOrders,
        shipmentVolume,
        systemMessages,
        totalClients,
        totalMonthlyOrders,
        totalMonthlyShipments,
        totalOrders,
        totalProjects,
        totalVendors,
        lastFiveStock,
        coopTotal,
        rebateTotal,
        dashboards,
        dailySales,
        monthlySales,
        weeklySales,
        ordersAtStep,
        alertOrders,
    };
};

/**
 * Retrives the user type (ADMIN, SALES)
 * @returns {Promise<*>}
 */
const getUserType = async (): Promise<any> => {
    const userModel = await getLocalUserData();

    return userModel.userType.name;
};

const getAccessType = () => {
    const userModel = getLocalUserDataFn();

    return get(userModel, "Access_Type.name", "");
};

const showInventoryDetails = (supplierModel: ISupplierModel, userModel: IUserUI): boolean => {
    let showInventoryDetails = true;

    if (supplierModel.hide_inventory_from_sales_reps) {
        if (userModel.supplierUserToken?.is_sales_rep) {
            showInventoryDetails = false;
        }
    }

    if (supplierModel.hide_inventory_from_vendors) {
        if (!userModel.isSupplierUser) {
            showInventoryDetails = false;
        }
    }

    return showInventoryDetails;
};

const hidePricing = (supplierModel: ISupplierModel, userModel: IUserUI, vendorModel: IVendorModel): boolean => {
    let hidePricing = false;

    if (supplierModel.has_hide_pricing) {
        if (userModel.isSupplierUser) {
            if (userModel.supplierUserToken?.hide_pricing) {
                hidePricing = true;
            }
        } else {
            if (vendorModel.hide_pricing) {
                hidePricing = true;
            }
        }
    }

    return hidePricing;
};

/**
 * Gets the current token for the user - either supplier or vendor depending on the user type.
 *
 * @param userModel
 */
const getCurrentToken = (userModel: IUserUI) => {
    if (userModel) {
        return userModel.isSupplierUser ? userModel.supplierUserToken : userModel.vendorUserToken;
    } else {
        return undefined;
    }
};

const getChangePasswordSchema = () => {
    return Yup.object().shape({
        old_password: Yup.string().required("Old Password is required"),
        new_password: Yup.string()
            .required("New Password is required")
            .min(8, "Password must be at least 8 characters long")
            .matches(/[a-z]/, "Password must contain at least one lowercase character")
            .matches(/[A-Z]/, "Password must contain at least one uppercase character")
            .matches(/[0-9]/, "Password must contain at least one number"),
        confirm_new_password: Yup.string()
            .required("Confirm New Password is required")
            .oneOf([Yup.ref("new_password")], "Passwords must match"),
    });
};

const getPreferencesSchema = () => {
    return Yup.object().shape({
        timezone: Yup.string(),
        phone: Yup.string().matches(/^\+?[0-9]{10,15}$/, "Phone number must be valid"),
        cell: Yup.string().matches(/^\+?[0-9]{10,15}$/, "Cell number must be valid"),
    });
};

const UserService = {
    clearUserData,
    getAccessType,
    getLocalUserData,
    getLocalUserDataFn,
    getUserType,
    parseDate,
    parseLastUpdated,
    parseUTCDate,
    performLogout,
    profileSaveUserData,
    saveUserData,
    transformDashboardData,
    transformUserData,
    getDashboardData,
    showInventoryDetails,
    hidePricing,
    parseUnixTimestamp,
    saveForceOnline,
    getForceOnline,
    getLocalAppConnectDataFn,
    setupLocalStorageLogoutEvent,
    getCurrentToken,
    getChangePasswordSchema,
    getPreferencesSchema,
};

export default UserService;
